import React, { useCallback } from 'react';

import { tourIdKey } from '@aline/common';
import { link, params } from '@aline/routes';
import { useNavigate } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import { CreateTemplate, SignAnyPdfIcon } from 'src/components/icons/GetStarted';
import { IntercomTour } from 'src/config';
import { Apollo } from 'src/gql/generated';

import { GenericAction } from '../StepAccordion';

export function LaunchNdaTemplate({ task }: { task: Apollo.OnboardingTask }) {
  const { launchAiPlaybookTour, loading } = useLaunchAiPlaybookTour();

  return (
    <GenericAction
      actionTitle="Run Example Playbook"
      image={<CreateTemplate />}
      loading={loading}
      text='Aline AI lets legal and business teams build "playbooks" for any contract (e.g., NDA, MSA, DPA, and Vendor Agreements) to cut review time by 5-10X. Launch an Example NDA to learn how playbooks work.'
      onClick={async () => {
        await launchAiPlaybookTour(task, IntercomTour.launchExampleNDA, {
          openExamplePlaybook: true,
        });
      }}
    />
  );
}

export function SignPDF({ task }: { task: Apollo.OnboardingTask }) {
  const { startTour } = useIntercom();
  const [updatedOnboardingTask, { loading: updatingTask }] =
    Apollo.useUpdateOnboardingTaskMutation();

  const handleLaunchTour = async () => {
    startTour(IntercomTour.signPDF);
    if (!task.completedAt) {
      await updatedOnboardingTask({
        variables: {
          input: {
            props: {
              completedAt: new Date().toISOString(),
            },
            taskId: task.id,
          },
        },
      });
    }
  };

  return (
    <GenericAction
      actionTitle="Use AlineSign"
      image={<SignAnyPdfIcon />}
      loading={updatingTask}
      text="AlineSign is an electronic signature solution compliant with the E-Sign Act that is built into Aline. You can sign an Aline document, PDF, or any other document format. Give it a try!"
      onClick={handleLaunchTour}
    />
  );
}

export function useLaunchAiPlaybookTour() {
  const navigate = useNavigate();
  const [updatedOnboardingTask, { loading: updatingTask }] =
    Apollo.useUpdateOnboardingTaskMutation();
  const [importExampleNDAResources, { loading: importingResources }] =
    Apollo.useImportExampleNdaResourcesMutation();
  /**
   * Imports the example NDA and opens it on the playbooks drawer so the tour
   * starts where it needs the user — with `openExamplePlaybook`, on the example
   * playbook itself (one click from Run); without it, on the playbook list
   * (one click from Auto-generate).
   */
  const launchAiPlaybookTour = useCallback(
    async (
      task: Pick<Apollo.OnboardingTask, 'id' | 'completedAt' | '__typename'>,
      tourId: number,
      options: { openExamplePlaybook: boolean }
    ) => {
      const { data } = await importExampleNDAResources();
      const settings = data?.importExampleNDAResources;
      if (settings?.exampleNDA) {
        const searchParams = new URLSearchParams({ [tourIdKey]: String(tourId) });
        if (options.openExamplePlaybook && settings.exampleNDAPlaybook) {
          searchParams.set(params.rightPanel.playbookId, settings.exampleNDAPlaybook.id);
        }
        navigate(
          `${link.DocumentDetailPlaybooks({ documentId: settings.exampleNDA.id })}?${searchParams}`
        );
      }

      if (!task.completedAt) {
        await updatedOnboardingTask({
          variables: {
            input: {
              props: {
                completedAt: new Date().toISOString(),
              },
              taskId: task.id,
            },
          },
        });
      }
    },
    [importExampleNDAResources, navigate, updatedOnboardingTask]
  );

  return { launchAiPlaybookTour, loading: importingResources || updatingTask };
}
